import React from 'react'

import { ClickAwayListener, Grow } from '@mui/material'

/**
 * The open/close behaviour of a panel anchored under a field — the filter
 * pickers' dropdowns.
 *
 * ## Why this exists
 *
 * Both of the app's hand-rolled anchored panels (`CertificatesSearchPicker`,
 * `CertificatesDateRangeFilter`) mounted on a bare `{open && <Box/>}`. A bare
 * conditional mount has no transition of any kind, so the panel did not appear —
 * it was simply there on the next frame. Against the platform, whose dropdowns
 * are MUI `Menu`s and therefore animate on MUI's default `Grow`, the app's read
 * as a flash.
 *
 * This is the SECOND site, which is the point at which the repo's own rule says
 * to extract rather than copy (working-conventions §36). It owns only the
 * lifecycle — mount, transition, click-away. The panel's own surface (position,
 * radius, border, shadow) stays with the caller, because the two anchor
 * differently: the search picker sits ON its trigger (`top: 0`), the date range
 * BELOW it (`top: 100%`).
 *
 * ## Matching the platform rather than inventing a motion
 *
 * `Grow` with `timeout="auto"` is literally what a MUI `Menu` uses — the
 * platform's `ActionDropdown` overrides neither, so it takes both defaults. Same
 * component, same duration curve, so the two apps cannot drift by one of them
 * picking a number.
 *
 * `transformOrigin: top` makes it expand downward out of the trigger instead of
 * from its own centre, which is what reads as rising into place.
 *
 * ## The mount flag, and why `unmountOnExit` alone will not do
 *
 * MUI's `ClickAwayListener` requires exactly one mounted child element — hand it
 * the `null` that `unmountOnExit` renders while closed and it throws. So the
 * panel is kept mounted for the length of the exit and torn down on `onExited`.
 * Without that the exit is instant while only the entrance animates, which is
 * the asymmetry you notice as a panel that glides in and vanishes.
 */
export type AnchoredPanelProps = {
  open: boolean
  onClickAway: () => void
  /** The panel itself. Must accept a `ref` and a `style` — an MUI `Box` does. */
  children: React.ReactElement
}

export const AnchoredPanel: React.FC<AnchoredPanelProps> = ({
  open,
  onClickAway,
  children,
}) => {
  const [mounted, setMounted] = React.useState(open)

  React.useEffect(() => {
    if (open) setMounted(true)
  }, [open])

  if (!mounted) return null

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Grow
        in={open}
        /*
          `appear` is load-bearing. The panel mounts with `in` already true, and
          a Transition with no `appear` treats a mounted-true as "already
          entered" and skips straight to the end state — which is the flash this
          component was written to remove, reproduced one layer up.
        */
        appear
        timeout='auto'
        onExited={() => setMounted(false)}
        style={{ transformOrigin: 'top' }}
      >
        {children}
      </Grow>
    </ClickAwayListener>
  )
}

export default AnchoredPanel
